import React from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

/**
 * AnimatedSection component that wraps sections with fade-in heading and slide-in container animations
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to be animated
 * @param {React.ReactNode} props.heading - Heading element to fade in
 * @param {string} props.className - Additional CSS classes for the section
 * @param {string} props.headingClassName - Additional CSS classes for the heading
 * @param {string} props.containerClassName - Additional CSS classes for the container
 * @param {number} props.delay - Delay in milliseconds before animation starts (default: 0)
 * @param {boolean} props.animateOnScroll - If false, content is visible immediately (no IntersectionObserver fade-in).
 */
const AnimatedSection = ({
  children,
  heading,
  className = '',
  headingClassName = '',
  containerClassName = '',
  delay = 0,
  id,
  animateOnScroll = true,
}) => {
  const { isVisible, elementRef } = useScrollAnimation({
    threshold: 0.15,
    rootMargin: '0px 0px -150px 0px',
    triggerOnce: true,
    disabled: !animateOnScroll,
  });

  return (
    <section ref={elementRef} id={id} className={className}>
      <div className={containerClassName}>
        {heading && (
          <div
            className={`transition-opacity duration-700 ease-out ${
              isVisible ? 'opacity-100' : 'opacity-0'
            } ${headingClassName}`}
            style={{
              transitionDelay: `${delay}ms`
            }}
          >
            {heading}
          </div>
        )}
        <div
          className={`transition-all duration-700 ease-out ${
            isVisible
              ? 'opacity-100 translate-x-0'
              : 'opacity-0 -translate-x-8'
          }`}
          style={{
            transitionDelay: `${delay + 100}ms`
          }}
        >
          {children}
        </div>
      </div>
    </section>
  );
};

export default AnimatedSection;

